import { useState, useEffect, useContext } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { ProposalService, Proposal, ProposalRequest } from "./proposal.service";
import { ProposalFormDataProvider } from "./proposalFormDataContext";
import CollapseHeader from "../../../core/common/collapse-header";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import male from "../../../../src/assets/male.png";
import axios from "axios";
import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders } from "../../../utils/authUtils";
import { AuthContext } from "../../../context/authcontext";
import ProposalForm from "./proposalForm";
import { useProposalFormData } from "./proposalFormDataContext";
import company from "../../../../src/assets/company.png";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import {
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import Activities from "../../../components/Tabs/Activities";
import Notes from "../../../components/Tabs/Notes";
import Attachments from "../../../components/Tabs/Attachments";
import Files from "../../../components/Tabs/Files";
import Tasks from "../../../components/Tabs/Tasks";
import ApprovalFlow from "../../../components/Tabs/ApprovalFlow";
import ProductsServices from "../../../components/Tabs/ProductsServices";
import Chat from "../../../components/Tabs/Chat";
import Tabs from "../../../components/Tabs/tabs";
import Email from "../../../components/Tabs/Email";
import { useEmailProviders } from "../../../hooks/useEmailProviders";
import ComposeEmail from "../../application/components/email/ComposeEmail";
import { useTenantChange } from "../../../hooks/useTenantChange";
import { getStageColorByOrder } from "../../../utils/stageColors";
import ContractForm from "../contracts/contractForm";
import { ContractFormDataProvider } from "../contracts/contractFormDataContext";
import {
  ContractService,
  ContractRequest,
} from "../contracts/contract.service";
import {
  getLifecycleTypeName,
  getLifecycleTypePluralName,
} from "../../../utils/lifecycleUtils";
import CustomFieldDisplay from "../../../components/CustomFieldDisplay";
import Tooltip from "@mui/material/Tooltip";
import SalesActivityModal from "../salesActivity/SalesActivityModal";
import { getApiErrorMessage } from "../../../utils/apiErrorUtils";

interface ProposalDetailsContentProps {
  isEditProposalOpen: boolean;
  setIsEditProposalOpen: (value: boolean) => void;
}

const ProposalDetailsContent = ({ isEditProposalOpen, setIsEditProposalOpen }: ProposalDetailsContentProps) => {
  const route = all_routes;
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedTenantId, auth } = useContext(AuthContext);
  const { proposalStatuses } = useProposalFormData();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updateMessage, setUpdateMessage] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editError, setEditError] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");
  const [showDeleteLoader, setShowDeleteLoader] = useState(false);
  const [activitiesRefreshTrigger, setActivitiesRefreshTrigger] = useState(0);
  const [stagesLoading, setStagesLoading] = useState(true);

  const [isCreatingContract, setIsCreatingContract] = useState(false);
  const [createContractError, setCreateContractError] = useState<string>("");
  const [resetContractFormTrigger, setResetContractFormTrigger] = useState(0);
  const [isCreateContractOpen, setIsCreateContractOpen] = useState(false);
  const [isDownloadingProposal, setIsDownloadingProposal] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [approvalStatusLoading, setApprovalStatusLoading] = useState(false);
  const [showSalesActivityModal, setShowSalesActivityModal] = useState(false);

  const { hasConnectedProviders, loading: emailProvidersLoading } =
    useEmailProviders();
  const [isComposeEmailOpen, setIsComposeEmailOpen] = useState(false);

  const handleSendEmail = () => {
    setIsComposeEmailOpen(true);
  };

  const handleCloseComposeEmail = () => {
    setIsComposeEmailOpen(false);
  };

  const handleMinimizeComposeEmail = () => {
    setIsComposeEmailOpen(false);
  };

  const getPrimaryEmail = () => {
    if (proposal?.primary_contact?.primary_email) {
      return proposal.primary_contact.primary_email;
    }
    return "";
  };

  const isEmailEnabled =
    !emailProvidersLoading && hasConnectedProviders && getPrimaryEmail() !== "";

  const getEmailDisabledReason = () => {
    if (emailProvidersLoading) return "Checking email integration...";
    if (!hasConnectedProviders) return "Email is not integrated";
    if (!proposal?.primary_contact)
      return `No contact attached to this ${getLifecycleTypeName("PROPOSAL").toLowerCase()}`;
    if (!getPrimaryEmail()) return "Attached contact has no email address";
    return "Email is not available";
  };

  const clearProposalData = () => {
    setProposal(null);
    setError(null);
    setLoading(true);
    setStagesLoading(true);
  };

  useTenantChange({
    onTenantChange: () => {
      clearProposalData();
      navigate("/proposals");
    },
    clearData: clearProposalData,
  });

  const createCompanyUrl = (proposal: any): string => {
    if (!proposal.company?.company_id) return "#";
    return `/companies/${proposal.company.company_id}`;
  };

  const createContactUrl = (proposal: any): string => {
    if (!proposal.primary_contact?.contact_id) return "#";
    return `/contacts/${proposal.primary_contact.contact_id}`;
  };

  const createDealUrl = (proposal: any): string => {
    if (!proposal.deal?.deal_id) return "#";
    return `/deals/${proposal.deal.deal_id}`;
  };

  const handleStatusUpdate = async (statusId: number | null) => {
    if (!proposal?.proposal_id || !selectedTenantId) return;

    setUpdatingStatus(true);
    setUpdateMessage(null);

    try {
      await axios.patch(
        `${API_BASE_URL}/proposal/${proposal.proposal_id}/`,
        { proposal_status_id: statusId, tenant_id: selectedTenantId },
        { headers: getAuthHeaders() }
      );

      const refreshResponse = await axios.get(
        `${API_BASE_URL}/proposal/${proposal.proposal_id}/?tenant_id=${selectedTenantId}`,
        { headers: getAuthHeaders() }
      );

      setProposal(refreshResponse.data);
      setUpdateMessage(
        `${getLifecycleTypeName("PROPOSAL")} status updated successfully!`
      );
      setActivitiesRefreshTrigger((prev) => prev + 1);
      setTimeout(() => setUpdateMessage(null), 3000);
    } catch (error) {
      console.error("Error updating proposal status:", error);
      setUpdateMessage(
        `Failed to update ${getLifecycleTypeName("PROPOSAL").toLowerCase()} status`
      );
      setTimeout(() => setUpdateMessage(null), 3000);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeleteProposal = async () => {
    if (!proposal?.proposal_id || isDeleting || !selectedTenantId) return;

    setIsDeleting(true);
    setDeleteError("");

    try {
      await ProposalService.deleteProposal(
        proposal.proposal_id,
        Number(selectedTenantId)
      );
      setOpenModal(false);
      setShowDeleteLoader(true);
      setTimeout(() => {
        setShowDeleteLoader(false);
        navigate(route.proposals);
      }, 1500);
    } catch (error: any) {
      console.error("Error deleting proposal:", error);
      setDeleteError(
        error.response?.data?.message ||
        `Failed to delete ${getLifecycleTypeName("PROPOSAL").toLowerCase()}. Please try again.`
      );
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteError("");
    setOpenModal(false);
  };

  const handleDownloadProposal = async () => {
    if (!proposal?.proposal_id || !selectedTenantId || isDownloadingProposal) return;

    setIsDownloadingProposal(true);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/proposal/${proposal.proposal_id}/generate_pdf_link/?tenant_id=${selectedTenantId}`,
        { headers: getAuthHeaders() }
      );

      const result = response.data;

      if (result.success && result.download_url) {
        // Fetch the PDF file from the S3 URL
        const pdfResponse = await fetch(result.download_url);
        if (!pdfResponse.ok) {
          throw new Error('Failed to fetch PDF file');
        }

        const pdfBlob = await pdfResponse.blob();

        // Create a blob URL and download the file
        const blobUrl = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = result.filename || `proposal_${proposal.proposal_id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL
        window.URL.revokeObjectURL(blobUrl);

        showSuccessAlert(
          `${getLifecycleTypeName("PROPOSAL")} PDF downloaded successfully!`
        );
      } else {
        throw new Error(result.message || "Failed to generate PDF download link");
      }
    } catch (error: any) {
      console.error("Error downloading proposal:", error);
      showErrorAlert(
        error.response?.data?.message ||
          error.message ||
          `Failed to download ${getLifecycleTypeName("PROPOSAL").toLowerCase()} PDF`
      );
    } finally {
      setIsDownloadingProposal(false);
    }
  };

  const handleCloseCreateContract = () => {
    setCreateContractError("");
    setResetContractFormTrigger((prev) => prev + 1);
    closeOffcanvas("offcanvas_create_contract");
  };

  const handleCreateContractSubmit = async (
    formData: Partial<ContractRequest>
  ) => {
    if (isCreatingContract) return;

    setIsCreatingContract(true);
    setCreateContractError("");

    try {
      const newContract = await ContractService.createContract(formData as any);
      closeOffcanvas("offcanvas_create_contract");
      setResetContractFormTrigger((prev) => prev + 1);

      setTimeout(() => {
        showSuccessAlert(
          `${getLifecycleTypeName("CONTRACT")} "${newContract.subject}" created successfully!`
        );
      }, 500);

      setTimeout(() => {
        navigate(`/contracts/${newContract.contract_id}`);
      }, 1000);

      return newContract;
    } catch (error: any) {
      console.error("Error creating contract:", error);
      setCreateContractError(getApiErrorMessage(error, `Failed to create ${getLifecycleTypeName("CONTRACT").toLowerCase()}. Please try again.`));
      showErrorAlert(getApiErrorMessage(error, `Failed to create ${getLifecycleTypeName("CONTRACT").toLowerCase()}. Please try again.`));
      throw error;
    } finally {
      setIsCreatingContract(false);
    }
  };

  const getPrefilledContractData = () => {
    if (!proposal) return {};

    return {
      subject: proposal.subject || "",
      description:
        proposal.description ||
        `${getLifecycleTypeName("CONTRACT")} for ${proposal.subject || getLifecycleTypeName("PROPOSAL")}`,
      amount: proposal.amount || undefined,
      currency_code: proposal.currency_code || "USD",
      company_id: proposal.company?.company_id || null,
      primary_contact_id: proposal.primary_contact?.contact_id || null,
      proposal_id: proposal.proposal_id || null,
      product_n_services: proposal.product_n_services || "",
      assigned_user_id: proposal.assigned_user?.user_id || null,
      assigned_team_id: proposal.assigned_team?.team_id || null,
      due_datetime: proposal.start_datetime || "",
      expiration_datetime: proposal.expiration_datetime || "",
      existingProductsServices: (proposal as any).product_n_service || [],
      existingAttachments: (proposal as any).attachments || [],
      company: proposal.company,
    };
  };

  const isStageActive = (statusId: number): boolean => {
    if (!proposal?.proposal_status?.proposal_status_id) return false;

    const currentStatusId = proposal.proposal_status.proposal_status_id;
    const currentStatus = proposalStatuses.find(
      (status) => status.proposal_status_id === currentStatusId
    );
    const checkStatus = proposalStatuses.find(
      (status) => status.proposal_status_id === statusId
    );

    if (!currentStatus || !checkStatus) return false;

    return (
      checkStatus.proposal_status_order <= currentStatus.proposal_status_order
    );
  };

  const handleEditSubmit = async (formData: Partial<ProposalRequest>) => {
    if (!proposal?.proposal_id || isUpdating) return;

    setIsUpdating(true);
    setEditError("");

    try {
      const updatedProposal = await ProposalService.updateProposal(
        proposal.proposal_id,
        formData as any
      );
      setProposal(updatedProposal);
      setActivitiesRefreshTrigger((prev) => prev + 1);

      closeOffcanvas("offcanvas_edit_proposal");

      setTimeout(() => {
        showSuccessAlert(
          `${getLifecycleTypeName("PROPOSAL")} "${updatedProposal.subject}" updated successfully!`
        );
      }, 200);
    } catch (error: any) {
      console.error("Error updating proposal:", error);
      setEditError(getApiErrorMessage(error, `Failed to update ${getLifecycleTypeName("PROPOSAL").toLowerCase()}. Please try again.`));
      showErrorAlert(getApiErrorMessage(error, `Failed to update ${getLifecycleTypeName("PROPOSAL").toLowerCase()}. Please try again.`));
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const offcanvas = document.getElementById(offcanvasId);
    if (offcanvas) {
      try {
        if ((window as any).bootstrap?.Offcanvas) {
          const bsOffcanvas = (window as any).bootstrap.Offcanvas.getInstance(
            offcanvas
          );
          if (bsOffcanvas) {
            bsOffcanvas.hide();
            return;
          }
        }

        const closeEvent = new Event("click");
        const closeButton = offcanvas.querySelector(
          '[data-bs-dismiss="offcanvas"]'
        );
        if (closeButton) {
          closeButton.dispatchEvent(closeEvent);
          return;
        }

        offcanvas.classList.remove("show");
        document.body.classList.remove("offcanvas-open");
        const backdrop = document.querySelector(".offcanvas-backdrop");
        if (backdrop) {
          backdrop.remove();
        }
      } catch (error) {
        console.warn("Error closing offcanvas:", error);
      }
    }
  };


  const DeletePreloader = () => (
    <div
      className="preloader"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <div className="loader"></div>
      <p className="mt-3 text-muted">
        {getLifecycleTypeName("PROPOSAL")} deleted successfully. Redirecting...
      </p>
    </div>
  );

  useEffect(() => {
    const fetchProposal = async () => {
      if (!id || !selectedTenantId) {
        setError(`${getLifecycleTypeName("PROPOSAL")} ID or tenant ID not provided`);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/proposal/${id}/?tenant_id=${selectedTenantId}`,
          { headers: getAuthHeaders() }
        );
        setProposal(response.data);
        setStagesLoading(false);
        setLoading(false);
      } catch (err: any) {
        console.error("Error fetching proposal details:", err);
        if (err?.response?.status === 404) {
          navigate(route.proposals);
          return;
        }
        setError(
          `Failed to fetch ${getLifecycleTypeName("PROPOSAL").toLowerCase()} details`
        );
        setLoading(false);
        setStagesLoading(false);
      }
    };

    fetchProposal();
  }, [id, selectedTenantId]);

  const refreshProposalData = async () => {
    const proposalId = id || proposal?.proposal_id;
    if (!proposalId || !selectedTenantId) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/proposal/${proposalId}/?tenant_id=${selectedTenantId}`,
        { headers: getAuthHeaders() }
      );
      setProposal(response.data);
    } catch (err: any) {
      console.error("Error refreshing proposal details:", err);
    }
  };

  useEffect(() => {
    const fetchApprovalStatus = async () => {
      if (!id || !selectedTenantId) return;
      try {
        setApprovalStatusLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/approval-policy-entity-instance/?tenant_id=${selectedTenantId}&related_entity_id=${id}&related_entity_name=proposal`,
          { headers: getAuthHeaders() }
        );
        const data = response.data;
        if (data.count === 0 || !data.results || data.results.length === 0) {
          setApprovalStatus("not_initiated_yet");
        } else {
          setApprovalStatus(data.results[0].status);
        }
      } catch (err) {
        console.error("Error fetching approval status:", err);
      } finally {
        setApprovalStatusLoading(false);
      }
    };

    fetchApprovalStatus();
  }, [id, selectedTenantId]);

  useEffect(() => {
    if (proposalStatuses.length > 0 && stagesLoading) {
      setStagesLoading(false);
    }
  }, [proposalStatuses, stagesLoading]);

  useEffect(() => {
    const editOffcanvas = document.getElementById("offcanvas_edit_proposal");

    const handleHidden = () => {
      setIsEditProposalOpen(false);
    };

    if (editOffcanvas) {
      editOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (editOffcanvas) {
        editOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);


  useEffect(() => {
    const createContractOffcanvas = document.getElementById(
      "offcanvas_create_contract"
    );

    const handleHidden = () => {
      setIsCreateContractOpen(false);
    };

    if (createContractOffcanvas) {
      createContractOffcanvas.addEventListener(
        "hidden.bs.offcanvas",
        handleHidden
      );
    }

    return () => {
      if (createContractOffcanvas) {
        createContractOffcanvas.removeEventListener(
          "hidden.bs.offcanvas",
          handleHidden
        );
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="page-title">
                      {getLifecycleTypeName("PROPOSAL")} Overview
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "400px" }}
                  >
                    <div className="text-center">
                      <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{ width: "3rem", height: "3rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5 className="text-muted">
                        Loading {getLifecycleTypeName("PROPOSAL").toLowerCase()}{" "}
                        details...
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="alert alert-danger" role="alert">
                <h5 className="alert-heading">Error</h5>
                <p className="mb-0">
                  {error || `${getLifecycleTypeName("PROPOSAL")} not found`}
                </p>
                <hr />
                <Link to={route.proposals} className="btn btn-outline-danger">
                  <i className="ti ti-arrow-narrow-left me-1" />
                  Back to {getLifecycleTypePluralName("PROPOSAL")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isDownloadEnabled = approvalStatus === "approved" || approvalStatus === "not_initiated_yet" || !approvalStatus;

  return (
    <>
      {showDeleteLoader && <DeletePreloader />}

      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="page-title">
                      {getLifecycleTypeName("PROPOSAL")} Overview
                    </h4>
                  </div>
                  <div className="col-sm-8 text-sm-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              {updateMessage && (
                <div className="row">
                  <div className="col-md-12">
                    <div
                      className={`alert ${updateMessage.includes("successfully")
                        ? "alert-success"
                        : "alert-danger"
                        } alert-dismissible fade show`}
                      role="alert"
                    >
                      {updateMessage}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setUpdateMessage(null)}
                      ></button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="contact-head">
                <div className="row align-items-center">
                  <div className="col-sm-6">
                    <ul className="contact-breadcrumb">
                      <li>
                        <Link to={route.proposals}>
                          <i className="ti ti-arrow-narrow-left" />
                          {getLifecycleTypePluralName("PROPOSAL")}
                        </Link>
                      </li>
                      <li>{proposal.subject && proposal.subject.length > 70 ? (
                        <Tooltip title={proposal.subject} placement="top" arrow>
                          <p className="mb-1" style={{ cursor: "default" }}>
                            {`${proposal.subject.substring(0, 70)}...`}
                          </p>
                        </Tooltip>
                      ) : (
                        <p className="mb-1">{proposal.subject}</p>
                      )}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body pb-2">
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <div className="d-flex align-items-center mb-2">
                      <div
                        className="avatar avatar-xl me-3 flex-shrink-0 border p-2 bg-light"
                        style={{ borderRadius: "50%" }}
                      >
                        <i
                          className="ti ti-file-text"
                          style={{ color: "#824eeb", fontSize: "3rem" }}
                        />
                      </div>
                      <div>
                        <h3 className="mb-1">{proposal.subject && proposal.subject.length > 20 ? (
                          <Tooltip title={proposal.subject} placement="top" arrow>
                            <span className="d-block mb-1" style={{ cursor: "default" }}>
                              {`${proposal.subject.substring(0, 20)}...`}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="d-block mb-1">{proposal.subject}</span>
                        )}</h3>
                        <p className="mb-1">
                          <i className="ti ti-building me-1" />
                          {proposal.company?.company_name || "No Company"}
                        </p>
                      </div>
                    </div>
                    <div className="contacts-action">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="mb-2">
                          <Select
                            options={proposalStatuses.map((status) => ({
                              value: status.proposal_status_id,
                              label: status.proposal_status_display_name,
                            }))}
                            value={
                              proposalStatuses.find(
                                (status) =>
                                  status.proposal_status_id ===
                                  proposal.proposal_status?.proposal_status_id
                              )
                                ? {
                                  value:
                                    proposal.proposal_status
                                      ?.proposal_status_id,
                                  label:
                                    proposal.proposal_status
                                      ?.proposal_status_display_name,
                                }
                                : null
                            }
                            onChange={(option) =>
                              handleStatusUpdate(option?.value || null)
                            }
                            placeholder="Select status"
                            isClearable={false}
                            isLoading={updatingStatus}
                            isDisabled={updatingStatus}
                            className="min-w-200"
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                minWidth: "200px",
                                backgroundColor: "#28a745",
                                borderColor: "#28a745",
                                color: "white",
                              }),
                              singleValue: (provided) => ({
                                ...provided,
                                color: "white",
                              }),
                              placeholder: (provided) => ({
                                ...provided,
                                color: "rgba(255, 255, 255, 0.8)",
                              }),
                              menu: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                              }),
                            }}
                          />
                        </div>
                        {isEmailEnabled ? (
                          <button
                            className="btn btn-secondary d-flex align-items-center mb-2 me-2"
                            onClick={handleSendEmail}
                            title="Send Email"
                          >
                            <i className="ti ti-mail me-2" />
                            Send Email
                          </button>
                        ) : (
                          <Tooltip title={getEmailDisabledReason()} placement="top" arrow>
                            <span
                              className="btn btn-secondary d-flex align-items-center mb-2 me-2"
                              style={{ cursor: "not-allowed", opacity: 0.5 }}
                            >
                              <i className="ti ti-mail me-2" />
                              Send Email
                            </span>
                          </Tooltip>
                        )}
                        <button
                          className="btn btn-primary d-flex align-items-center mb-2 me-2"
                          title={`Create ${getLifecycleTypeName("CONTRACT")}`}
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_create_contract"
                          onClick={() => setIsCreateContractOpen(true)}
                        >
                          <i className="ti ti-file-certificate me-2" />
                          Create {getLifecycleTypeName("CONTRACT")}
                        </button>
                        <Link
                          to="#"
                          className="btn-icon d-flex align-items-center justify-content-center mb-2"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_edit_proposal"
                          title={`Edit ${getLifecycleTypeName("PROPOSAL")}`}
                          onClick={() => setIsEditProposalOpen(true)}
                        >
                          <i className="ti ti-edit-circle fs-16" />
                        </Link>
                        <Link
                          className="btn-icon d-flex align-items-center justify-content-center mb-2"
                          to="#"
                          onClick={(e) => { e.preventDefault(); setShowSalesActivityModal(true); }}
                          title="Log Activity"
                        >
                          <i className="ti ti-clipboard-list fs-16" />
                        </Link>
                        <div className="dropdown">
                          <Link
                            to="#"
                            className="btn-icon d-flex align-items-center justify-content-center mb-2"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            title="More Actions"
                          >
                            <i className="ti ti-dots-vertical fs-16" />
                          </Link>
                          <div className="dropdown-menu dropdown-menu-end dropup">
                            <Link
                              className={`dropdown-item d-flex align-items-center ${!isDownloadEnabled ? "disabled" : ""}`}
                              to="#"
                              onClick={(e) => {
                                e.preventDefault();
                                if (isDownloadEnabled) {
                                  handleDownloadProposal();
                                }
                              }}
                              style={{
                                opacity: isDownloadingProposal || !isDownloadEnabled ? 0.6 : 1,
                                cursor: !isDownloadEnabled ? "not-allowed" : "pointer"
                              }}
                            >
                              {isDownloadingProposal ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <i className="ti ti-file-download text-blue-light me-2" />
                                  Download {getLifecycleTypeName("PROPOSAL")}
                                </>
                              )}
                            </Link>

                            <Link
                              className="dropdown-item d-flex align-items-center"
                              to="#"
                              onClick={() => setOpenModal(true)}
                            >
                              <i className="ti ti-trash text-danger me-2" />
                              Delete
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 theiaStickySidebar">
              <div className="card">
                <div className="card-body p-3">
                  <h6 className="mb-3 fw-semibold">
                    {getLifecycleTypePluralName("PROPOSAL")} Information
                  </h6>
                  <ul>
                    <li className="row mb-3">
                      <span className="col-6">Amount</span>
                      <span className="col-6">
                        {proposal.grand_total
                          ? `${proposal.currency_code || ""
                          } ${Number(proposal.grand_total || 0).toFixed(4)}`
                          : "Not specified"}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Start Date</span>
                      <span className="col-6">
                        {formatCustomDateTime(proposal.start_datetime || null)}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Expiration Date</span>
                      <span className="col-6">
                        {formatCustomDateTime(
                          proposal.expiration_datetime || null
                        )}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">
                        {getLifecycleTypeName("PROPOSAL")} Status
                      </span>
                      <span className="col-6">
                        <span
                          className={`badge ${getStageColorByOrder(
                            proposal.proposal_status?.proposal_status_order || 0
                          )}`}
                        >
                          {proposal.proposal_status
                            ?.proposal_status_display_name || "Not Set"}
                        </span>
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Approval Status</span>
                      <span className="col-6">
                        {approvalStatusLoading ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <span
                            className={`badge ${approvalStatus === "approved"
                              ? "bg-success"
                              : approvalStatus === "pending"
                                ? "bg-warning"
                                : approvalStatus === "rejected"
                                  ? "bg-danger"
                                  : approvalStatus === "in_progress"
                                    ? "bg-primary"
                                    : approvalStatus === "not_initiated_yet"
                                      ? "bg-info"
                                      : "bg-secondary"
                              }`}
                          >
                            {approvalStatus === "approved" ? "Approved" : approvalStatus === "pending" ? "Pending" : approvalStatus === "rejected" ? "Rejected" : approvalStatus === "in_progress" ? "In Progress" : approvalStatus === "not_initiated_yet" ? "Not Initiated Yet" : "Not Set"}
                          </span>
                        )}
                      </span>
                    </li>
                  </ul>
                  <CustomFieldDisplay
                    customFields={(proposal as any)?.custom_fields}
                    showTitle={true}
                  />
                  <h6 className="mb-3 fw-semibold">Assigned Team</h6>
                  <div className="d-flex align-items-center mb-3">
                    <div
                      className="avatar avatar-md me-2 d-flex align-items-center justify-content-center bg-light"
                      style={{ width: 40, height: 40, borderRadius: "50%" }}
                    >
                      <i className="ti ti-users fs-24 text-primary" />
                    </div>
                    <div>
                      <span className="fw-semibold">
                        {proposal.assigned_team?.team_name || "Not Assigned"}
                      </span>
                    </div>
                  </div>
                  <hr />

                  <h6 className="mb-3 fw-semibold">Assigned User</h6>
                  <div className="d-flex align-items-center mb-3">
                    <span
                      className="avatar avatar-sm border rounded-circle p-1 me-2"
                      style={{ borderRadius: "50%" }}
                    >
                      <img
                        src={proposal.assigned_user?.avatar_url || male}
                        alt="Image"
                        style={{
                          width: "25px",
                          height: "25px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    </span>
                    <p>{proposal.assigned_user?.full_name ?? "Not Assigned"}</p>
                  </div>
                  <hr />

                  <h6 className="mb-3 fw-semibold">Company</h6>
                  <div className="d-flex align-items-center">
                    <span
                      className="avatar avatar-sm border rounded-circle p-1 me-2"
                      style={{ borderRadius: "50%" }}
                    >
                      <img
                        src={proposal.company?.avatar_url || company}
                        alt="Image"
                        style={{ borderRadius: "50%" }}
                      />
                    </span>
                    <div
                      style={{
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                    >
                      <Link to={createCompanyUrl(proposal)} target="_blank">
                        {proposal.company?.company_name || "Not specified"}
                      </Link>
                    </div>
                  </div>
                  <hr />

                  <h6 className="mb-3 fw-semibold">Contact</h6>
                  <div className="d-flex align-items-center mb-3">
                    <span
                      className="avatar avatar-sm border rounded-circle p-1 me-2"
                      style={{ borderRadius: "50%" }}
                    >
                      <img
                        src={proposal.primary_contact?.avatar_url || male}
                        alt="Image"
                        style={{
                          width: "25px",
                          height: "25px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    </span>
                    <Link
                      to={createContactUrl(proposal)}
                      target="_blank"
                      style={{ textDecoration: "underline" }}
                    >
                      {proposal.primary_contact?.full_name || "Unassigned"}
                    </Link>
                  </div>
                  <ul>
                    <li className="row mb-3">
                      <span className="col-6">Primary Phone</span>
                      <span className="col-6">
                        {proposal.primary_contact?.primary_phone || "Not specified"}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Primary Email</span>
                      <span className="col-6">
                        {proposal.primary_contact?.primary_email || "Not specified"}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Job Title</span>
                      <span className="col-6">
                        {proposal.primary_contact?.job_title || "Not specified"}
                      </span>
                    </li>
                  </ul>
                  <hr />

                  <h6 className="mb-3 fw-semibold">Related {getLifecycleTypeName("DEAL")}</h6>
                  <div className="d-flex align-items-center mb-3">
                    <div
                      className="avatar avatar-md flex-shrink-0 border p-2 bg-light"
                      style={{ borderRadius: "50%" }}
                    >
                      <i
                        className="ti ti-medal mt-2"
                        style={{ color: "#824eeb", fontSize: "1.5rem" }}
                      />
                    </div>
                    <div
                      style={{
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                    >
                      <Link
                        to={createDealUrl(proposal)}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/deals/${proposal.deal?.deal_id}`, {
                            state: { dealData: proposal.deal },
                          });
                        }}
                      >
                        {proposal.deal?.title && proposal.deal?.title.length > 45 ? (
                          <Tooltip title={proposal.deal.title} placement="top" arrow>
                            <span className="d-block mb-1" style={{ cursor: "pointer" }}>
                              {`${proposal.deal?.title?.substring(0, 45)}...`}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="d-block mb-1">{proposal.deal?.title || `No Related ${getLifecycleTypeName("DEAL")}`}</span>
                        )}
                      </Link>
                    </div>
                  </div>
                  <hr />

                  <h6 className="mb-3 fw-semibold">
                    {getLifecycleTypeName("PROPOSAL")} Description
                  </h6>
                  <div className="proposal-description mb-3">
                    <p className="text-muted mb-0">
                      {proposal.description || "No description provided"}
                    </p>
                  </div>
                  <hr />

                  {/* Tags Section */}

                  <>
                    <h6 className="mb-3 fw-semibold">Tags</h6>
                    <ul className="social-info">
                      <li style={{ padding: '5px' }}>
                        {
                          proposal?.tags?.insert && Array.isArray(proposal.tags.insert) && proposal.tags.insert.length > 0 ? (
                            proposal.tags.insert.map((tag: any, index: number) => (
                              <span key={tag.entity_tag_id || index}
                                className="badge me-1"
                                style={{
                                  backgroundColor: '#824EEB',
                                  color: 'white'
                                }}>
                                {tag.entity_tag_name}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted">No Tags Assigned</span>
                          )
                        }
                      </li>
                    </ul>
                  </>
                  <hr />

                  <ul>
                    <li className="row mb-3">
                      <span className="col-6">Created At</span>
                      <span className="col-6">
                        {formatCustomDateTime(proposal.created_datetime)}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Created By</span>
                      <span className="col-6 d-flex align-items-center">
                        <span className="avatar avatar-xs me-1">
                          <img
                            src={proposal.created_by?.avatar_url || male}
                            alt="Image"
                            style={{
                              width: "25px",
                              height: "20px",
                              borderRadius: "50%",
                            }}
                          />
                        </span>
                        <span>
                          {proposal.created_by
                            ? `${proposal.created_by.full_name}`
                            : "No User Found"}
                        </span>
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Last Modified</span>
                      <span className="col-6">
                        {formatCustomDateTime(proposal.updated_datetime)}
                      </span>
                    </li>
                    <li className="row mb-0">
                      <span className="col-6">Modified By</span>
                      <span className="col-6 d-flex align-items-center">
                        <span className="avatar avatar-xs me-1">
                          <img
                            src={proposal.updated_by?.avatar_url || male}
                            alt="Image"
                            style={{
                              width: "25px",
                              height: "20px",
                              borderRadius: "50%",
                            }}
                          />
                        </span>
                        {proposal.updated_by
                          ? `${proposal.updated_by.full_name}`
                          : "No User Found"}
                      </span>
                    </li>
                  </ul>

                </div>
              </div>
            </div>

            <div className="col-xl-9">
              <div className="card mb-3">
                <div className="card-body pb-0">
                  <h4 className="fw-semibold mb-3">
                    {getLifecycleTypeName("PROPOSAL")} Pipeline Status
                  </h4>
                  {stagesLoading ? (
                    <div className="d-flex align-items-center justify-content-center py-3">
                      <div
                        className="spinner-border spinner-border-sm text-primary me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span className="text-muted">
                        Loading pipeline stages...
                      </span>
                    </div>
                  ) : proposalStatuses.length > 0 ? (
                    <div className="pipeline-list">
                      <ul>
                        {proposalStatuses.map((status) => (
                          <li key={status.proposal_status_id}>
                            <Link
                              to="#"
                              className={
                                isStageActive(status.proposal_status_id)
                                  ? getStageColorByOrder(
                                    status.proposal_status_order
                                  )
                                  : "bg-gray"
                              }
                              style={{
                                pointerEvents: isStageActive(
                                  status.proposal_status_id
                                )
                                  ? "auto"
                                  : "none",
                                color: isStageActive(status.proposal_status_id)
                                  ? ""
                                  : "#fff",
                              }}
                            >
                              {status.proposal_status_display_name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <span className="text-muted">
                        No pipeline stages found
                      </span>
                    </div>
                  )}
                  <Tabs
                    showEmailTab={true}
                    isProposalPage={true}
                    showProductsServicesTab={true}
                    showApprovalFlowTab={true}
                    showChatTab={true}
                    hasApprovalAction={
                      Array.isArray(proposal?.approvers) &&
                      proposal.approvers.includes(auth?.user_id)
                    }
                  />
                </div>
              </div>

              <div className="tab-content pt-0">
                <div className="tab-pane fade show active" id="activities">
                  <Activities
                    tenant_id={selectedTenantId}
                    related_entity_id={proposal?.proposal_id}
                    refreshTrigger={activitiesRefreshTrigger}
                    related_entity_name="proposal"
                    crm_entity_id={proposal?.crm_entity_id}
                  />
                </div>

                <Notes
                  tenant_id={selectedTenantId}
                  related_entity_id={proposal?.proposal_id}
                  related_entity_name="proposal"
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                />

                <Attachments
                  tenant_id={selectedTenantId}
                  related_entity_id={proposal?.proposal_id}
                  related_entity_name="proposal"
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                />

                <Files
                  tenant_id={selectedTenantId}
                  related_entity_id={proposal?.proposal_id}
                  related_entity_name="proposal"
                  refreshTrigger={activitiesRefreshTrigger}
                />

                <Tasks
                  tenant_id={selectedTenantId}
                  related_entity_id={proposal?.proposal_id}
                  related_entity_name="proposal"
                  refreshTrigger={activitiesRefreshTrigger}
                />

                <ProductsServices
                  tenant_id={selectedTenantId}
                  related_entity_id={proposal?.proposal_id}
                  related_entity_name="proposal"
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                  onEntityUpdate={refreshProposalData}
                  currencyCode={proposal?.currency_code}
                />

                <ApprovalFlow
                  tenant_id={selectedTenantId}
                  related_entity_id={proposal?.proposal_id}
                  related_entity_name="proposal"
                  currentUserId={auth?.user_id}
                />

                <Email
                  contactIds={
                    proposal?.primary_contact?.contact_id
                      ? [proposal.primary_contact.contact_id]
                      : []
                  }
                />

                <Chat
                  tenant_id={selectedTenantId}
                  contact_id={proposal?.primary_contact?.contact_id}
                  isContactPage={true}
                  lifecycleName="proposal"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={openModal} onHide={() => setOpenModal(false)}>
        <div className="modal-body">
          <div className="text-center">
            <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
              <i className="ti ti-trash-x fs-36 text-danger" />
            </div>
            <h4 className="mb-2">
              Delete {getLifecycleTypeName("PROPOSAL")}?
            </h4>
            <p className="mb-0">
              Are you sure you want to delete this{" "}
              {getLifecycleTypeName("PROPOSAL").toLowerCase()}? This action
              cannot be undone.
            </p>
            {deleteError && (
              <div className="alert alert-danger mt-3" role="alert">
                {deleteError}
              </div>
            )}
            <div className="d-flex align-items-center justify-content-center mt-4">
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteProposal}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <SalesActivityModal
        isVisible={showSalesActivityModal}
        onClose={() => setShowSalesActivityModal(false)}
        crmEntityId={proposal?.crm_entity_id}
        recordId={proposal?.proposal_id}
        onSuccess={() => setActivitiesRefreshTrigger((prev) => prev + 1)}
      />

      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_edit_proposal"
        style={{ width: "550px" }}

      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">
            Edit {getLifecycleTypeName("PROPOSAL")}
          </h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {editError && (
            <div className="alert alert-danger" role="alert">
              {editError}
            </div>
          )}
          {isEditProposalOpen && (
            <ProposalForm
              proposal={proposal}
              onSubmit={handleEditSubmit}
              onCancel={() => closeOffcanvas("offcanvas_edit_proposal")}
              isMainApiCompleted={true}
              isSubmitting={isUpdating}
              formId="edit-proposal-details"
            />
          )}
        </div>
      </div>

      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_create_contract"
        style={{ width: "550px" }}

      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Create {getLifecycleTypeName("CONTRACT")}</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {createContractError && (
            <div className="alert alert-danger" role="alert">
              {createContractError}
            </div>
          )}
          {isCreateContractOpen && (
            <ContractFormDataProvider isMainApiCompleted={true}>
              <ContractForm
                key={`create-contract-${resetContractFormTrigger}`}
                onSubmit={handleCreateContractSubmit}
                onCancel={handleCloseCreateContract}
                isSubmitting={isCreatingContract}
                resetTrigger={resetContractFormTrigger}
                prefilledData={getPrefilledContractData()}
                onRefreshList={async () => { }}
              />
            </ContractFormDataProvider>
          )}
        </div>
      </div>

      <ComposeEmail
        isOpen={isComposeEmailOpen}
        onClose={handleCloseComposeEmail}
        onMinimize={handleMinimizeComposeEmail}
        draftData={{
          send_to: getPrimaryEmail(),
          subject: `Re: ${proposal?.subject || getLifecycleTypeName("PROPOSAL")}`,
        }}
        readOnlyRecipient={true}
      />
    </>
  );
};

const ProposalDetails = () => {
  const { selectedTenantId } = useContext(AuthContext);
  const [isEditProposalOpen, setIsEditProposalOpen] = useState(false);

  return (
    <ProposalFormDataProvider
      isMainApiCompleted={true}
      shouldLoad={isEditProposalOpen}
      loadStatusesOnly={true}
    >
      <ProposalDetailsContent
        isEditProposalOpen={isEditProposalOpen}
        setIsEditProposalOpen={setIsEditProposalOpen}
      />
    </ProposalFormDataProvider>
  );
};

export default ProposalDetails;
